import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
	Box,
	Drawer,
	AppBar,
	Toolbar,
	List,
	Typography,
	Divider,
	IconButton,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Avatar,
	Menu,
	MenuItem,
	Chip,
	Tooltip,
	alpha,
	useMediaQuery,
} from "@mui/material";
import {
	Menu as MenuIcon,
	MenuOpen as MenuOpenIcon,
	Dashboard as DashboardIcon,
	Folder as FolderIcon,
	PlayCircle as PlayCircleIcon,
	Analytics as AnalyticsIcon,
	Settings as SettingsIcon,
	Logout as LogoutIcon,
	AccountCircle,
	Science as ScienceIcon,
	Http as HttpIcon,
	Work as WorkIcon,
	ViewList as ViewListIcon,
	Storage as StorageIcon,
	PlaylistPlay as PlaylistPlayIcon,
	HelpOutline as HelpOutlineIcon,
	Business as BusinessIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { logout, setCurrentUser } from "../store/slices/authSlice";
import api from "../services/api";
import { isPlatformToken } from "../utils/session";
import { motion } from "framer-motion";
import { ThemeToggle } from "./ThemeMode";

const drawerWidth = 260;
// Icons only (the menu collapsed), per browser like the theme
const collapsedWidth = 72;
const COLLAPSED_KEY = "sidebarCollapsed";

const readCollapsed = () => {
	try {
		return localStorage.getItem(COLLAPSED_KEY) === "true";
	} catch {
		return false;
	}
};

const menuItems = [
	{ text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
	{ text: "Projects", icon: <WorkIcon />, path: "/projects" },
	{ text: "Tests", icon: <ScienceIcon />, path: "/tests" },
	{ text: "APIs", icon: <HttpIcon />, path: "/apis" },
	{ text: "Suites", icon: <ViewListIcon />, path: "/suites" },
	{
		text: "Suite Runs",
		icon: <PlaylistPlayIcon />,
		path: "/suite-executions",
	},
	{ text: "Data Sets", icon: <StorageIcon />, path: "/datasets" },
	{ text: "Executions", icon: <PlayCircleIcon />, path: "/executions" },
	{ text: "Analytics", icon: <AnalyticsIcon />, path: "/analytics" },
	{ text: "Settings", icon: <SettingsIcon />, path: "/settings" },
	{ text: "Help", icon: <HelpOutlineIcon />, path: "/help" },
];

// A platform operator manages organisations and sees nothing else
const platformMenuItems = [
	{ text: "Organisations", icon: <BusinessIcon />, path: "/platform" },
];

export default function Layout({ children }) {
	const [mobileOpen, setMobileOpen] = useState(false);
	const [collapsed, setCollapsed] = useState(readCollapsed);
	const [anchorEl, setAnchorEl] = useState(null);
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const { user } = useSelector((state) => state.auth);
	const platform = isPlatformToken();
	const items = platform ? platformMenuItems : menuItems;
	// The phone drawer slides over the page and is always full width
	const desktop = useMediaQuery((theme) => theme.breakpoints.up("sm"));
	const narrow = desktop && collapsed;
	const navWidth = narrow ? collapsedWidth : drawerWidth;

	// Who is signed in, with their current role (it isn't kept across reloads,
	// and an admin may change it meanwhile). What they may do with each item
	// comes from the backend's canModify / canStop; this only drives the UI.
	useEffect(() => {
		const load = () =>
			api
				.get(isPlatformToken() ? "/platform/me" : "/users/me")
				.then((res) => dispatch(setCurrentUser(res.data)))
				.catch(() => {});
		load();
		window.addEventListener("focus", load);
		return () => window.removeEventListener("focus", load);
	}, [dispatch]);

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	const handleCollapseToggle = () => {
		setCollapsed((was) => {
			try {
				localStorage.setItem(COLLAPSED_KEY, String(!was));
			} catch {
				// Not remembered, still toggled
			}
			return !was;
		});
	};

	const handleMenuOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleLogout = () => {
		dispatch(logout());
		navigate("/login");
	};

	const drawerContent = (narrow) => (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflowX: "hidden" }}>
			<Toolbar sx={{ px: narrow ? 2.25 : 2.5 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						cursor: "pointer",
					}}
					onClick={() => navigate("/")}
				>
					<Box
						sx={{
							width: 34,
							height: 34,
							borderRadius: "8px",
							background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
							boxShadow: "0 2px 8px rgba(124, 58, 237, 0.35)",
						}}
					>
						<svg
							width="18"
							height="18"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
								stroke="white"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</Box>
					<Box sx={{ position: "relative", display: narrow ? "none" : "inline-block" }}>
						<Typography
							variant="h6"
							fontWeight={800}
							sx={{
								background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 55%, #a855f7 100%)",
								WebkitBackgroundClip: "text",
								WebkitTextFillColor: "transparent",
								backgroundClip: "text",
								letterSpacing: "-0.02em",
								lineHeight: 1.2,
							}}
						>
							Testify
						</Typography>
						<Typography
							sx={{
								position: "absolute",
								top: -5,
								right: -2,
								fontSize: "7px",
								fontWeight: 700,
								color: "#7c3aed",
								letterSpacing: "0.07em",
								textTransform: "uppercase",
								lineHeight: 1,
								opacity: 0.85,
							}}
						>
							SAARKA's
						</Typography>
					</Box>
				</Box>
			</Toolbar>
			<Divider />
			<List sx={{ flex: 1, px: 1.5, py: 2 }}>
				{items.map((item) => {
					const isActive = location.pathname === item.path;
					return (
						<ListItem
							key={item.text}
							disablePadding
							sx={{ mb: 0.5 }}
						>
							<Tooltip title={narrow ? item.text : ""} placement="right">
								<ListItemButton
									onClick={() => navigate(item.path)}
									aria-label={narrow ? item.text : undefined}
									sx={{
										borderRadius: 2,
										py: 1.2,
										px: narrow ? 1.5 : 2,
										bgcolor: isActive
											? alpha("#4f46e5", 0.1)
											: "transparent",
										color: isActive
											? "primary.main"
											: "text.secondary",
										"&:hover": {
											bgcolor: isActive
												? alpha("#4f46e5", 0.15)
												: "action.hover",
										},
										transition: "all 0.2s ease-in-out",
									}}
								>
									<ListItemIcon
										sx={{
											color: isActive
												? "primary.main"
												: "text.secondary",
											minWidth: narrow ? 0 : 40,
										}}
									>
										{item.icon}
									</ListItemIcon>
									{!narrow && (
										<ListItemText
											primary={item.text}
											primaryTypographyProps={{
												fontWeight: isActive ? 600 : 500,
												fontSize: "0.9rem",
												noWrap: true,
											}}
										/>
									)}
								</ListItemButton>
							</Tooltip>
						</ListItem>
					);
				})}
			</List>
		</Box>
	);

	return (
		<Box sx={{ display: "flex" }}>
			<AppBar
				position="fixed"
				elevation={0}
				sx={{
					width: { sm: `calc(100% - ${navWidth}px)` },
					ml: { sm: `${navWidth}px` },
					transition: (theme) => theme.transitions.create(["width", "margin"]),
					bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8),
					backdropFilter: "blur(8px)",
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Toolbar>
					<IconButton
						color="inherit"
						aria-label="open drawer"
						edge="start"
						onClick={handleDrawerToggle}
						sx={{
							mr: 2,
							display: { sm: "none" },
							color: "text.primary",
						}}
					>
						<MenuIcon />
					</IconButton>
					<Tooltip title={collapsed ? "Expand menu" : "Collapse menu"}>
						<IconButton
							aria-label={collapsed ? "Expand menu" : "Collapse menu"}
							edge="start"
							onClick={handleCollapseToggle}
							sx={{
								mr: 2,
								display: { xs: "none", sm: "inline-flex" },
								color: "text.secondary",
							}}
						>
							{collapsed ? <MenuIcon /> : <MenuOpenIcon />}
						</IconButton>
					</Tooltip>
					<Box sx={{ flexGrow: 1 }} />
					<ThemeToggle sx={{ mr: 1 }} />
					{/* Which organisation this is (or the platform console) */}
					<Chip
						icon={<BusinessIcon fontSize="small" />}
						label={platform ? "Platform console" : user?.tenantName || ""}
						size="small"
						variant="outlined"
						sx={{
							mr: 2,
							display: platform || user?.tenantName ? "inline-flex" : "none",
							maxWidth: 240,
						}}
					/>
					<IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
						<Avatar
							src={user?.avatarUrl || undefined}
							sx={{
								bgcolor: "primary.main",
								width: 40,
								height: 40,
								fontSize: "0.85rem",
								fontWeight: 600,
							}}
						>
							{`${user?.firstName?.charAt(0) || ""}${user?.lastName?.charAt(0) || ""}`.toUpperCase() ||
								"U"}
						</Avatar>
					</IconButton>
					<Menu
						anchorEl={anchorEl}
						open={Boolean(anchorEl)}
						onClose={handleMenuClose}
						transformOrigin={{
							horizontal: "right",
							vertical: "top",
						}}
						anchorOrigin={{
							horizontal: "right",
							vertical: "bottom",
						}}
						PaperProps={{
							sx: {
								mt: 1,
								minWidth: 200,
								borderRadius: 2,
							},
						}}
					>
						{!platform && (
							<MenuItem
								onClick={() => {
									navigate("/settings");
									handleMenuClose();
								}}
							>
								<ListItemIcon>
									<AccountCircle fontSize="small" />
								</ListItemIcon>
								<ListItemText>Profile</ListItemText>
							</MenuItem>
						)}
						{!platform && <Divider />}
						<MenuItem onClick={handleLogout}>
							<ListItemIcon>
								<LogoutIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText>Logout</ListItemText>
						</MenuItem>
					</Menu>
				</Toolbar>
			</AppBar>
			<Box
				component="nav"
				sx={{
					width: { sm: navWidth },
					flexShrink: { sm: 0 },
					transition: (theme) => theme.transitions.create("width"),
				}}
			>
				<Drawer
					variant="temporary"
					open={mobileOpen}
					onClose={handleDrawerToggle}
					ModalProps={{
						keepMounted: true,
					}}
					sx={{
						display: { xs: "block", sm: "none" },
						"& .MuiDrawer-paper": {
							boxSizing: "border-box",
							width: drawerWidth,
						},
					}}
				>
					{drawerContent(false)}
				</Drawer>
				<Drawer
					variant="permanent"
					sx={{
						display: { xs: "none", sm: "block" },
						"& .MuiDrawer-paper": {
							boxSizing: "border-box",
							width: navWidth,
							overflowX: "hidden",
							transition: (theme) => theme.transitions.create("width"),
							borderRight: 1,
							borderColor: "divider",
						},
					}}
					open
				>
					{drawerContent(narrow)}
				</Drawer>
			</Box>
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					p: 3,
					width: { sm: `calc(100% - ${navWidth}px)` },
					transition: (theme) => theme.transitions.create("width"),
					minHeight: "100vh",
					bgcolor: "background.default",
				}}
			>
				<Toolbar />
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: -20 }}
					transition={{ duration: 0.3 }}
				>
					{children}
				</motion.div>
			</Box>
		</Box>
	);
}
